const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/modules/CIS4m/components/CIS4mMetaKatDropDown.tsx';
import * as React from 'react';
import { forwardRef, useMemo } from 'react';
import { useCIS4mValidation } from '../hooks';
import { CIS4mValueComponentProps } from '../types';
import { SortFunction } from '../../../utils/data';
import { UseRemoteData2Result } from '../../../utils/redux4Requests';
import { TextInput, useOptionalText } from '../../../utils/intlUtils';
import { useCIS4mContext } from '../context';
import { MetaKatItem } from '../../../defs/kisim';
import { useFormMetaKatData } from '../../../components/Forms/store';
import { MetaKatItemBasicSelect } from '../../../components/Forms/components/MetaKat/MetaKatItemBasicSelect';
import { cx } from '@emotion/css';
import { metaKatItemToMetaKatItemRef } from '../../../utils/kisim';
import _ from 'lodash';
import { findMetaKatItemByValue } from '../../../components/Forms/components/MetaKat/utils';
import { Map } from 'immutable';

interface Props extends CIS4mValueComponentProps {
    checkValidity?: () => string;
    className?: string;
    dataHook?: (pcode: string, lookup: boolean) => UseRemoteData2Result<MetaKatItem[] | undefined>;
    favorites?: string[]; // can only used in combination with valuePath pointing to a string
    filter?: (item: MetaKatItem) => boolean;
    label?: TextInput;
    labelPath?: string;
    lookup?: boolean;
    noValueLabel?: TextInput; // Text to be shown for the option that has no value
    pcode?: string;
    required?: boolean;
    sort?: SortFunction<MetaKatItem>;
    valuePath?: string;
}

export const CIS4mMetaKatDropDown = forwardRef((props: Props, ref: any) => {
    const {
        checkValidity,
        dataHook,
        disabled = false,
        filter = (item: MetaKatItem) => true,
        hidden = false,
        initialValue,
        lookup = true,
        path,
        pcode,
        required = false,
        valuePath,
    } = props;

    const { data: metaKatList, isPendingOrLoading } = dataHook
        ? dataHook(pcode ?? '', lookup)
        : useFormMetaKatData(pcode ?? '', lookup);

    const filteredItems = useMemo(() => (metaKatList ? metaKatList.filter(filter) : []), [metaKatList, filter]);

    const [cis4mValue, setValue, errorMessage] = useCIS4mValidation(ref, {
        checkValidity,
        disabled,
        hidden,
        initialValue: initialValue ? filteredItems.find(findMetaKatItemByValue(initialValue, valuePath)) : undefined,
        path,
        required,
    });

    const { variant } = useCIS4mContext().style;

    const label = useOptionalText(props.label);

    if (hidden) return null;
    if (isPendingOrLoading) return `${label ?? 'CIS4mMetaKatDropDown'} (loading)`;
    if (filteredItems.length === 0) return `${label ?? 'CIS4mMetaKatDropDown'} (no data)`;

    const handleChange = (value: MetaKatItem | undefined) => {
        if (valuePath) {
            setValue(value ? _.get(value, valuePath) : undefined);
        } else {
            setValue(value ? metaKatItemToMetaKatItemRef(value) : undefined);
        }
    };

    const value = Map.isMap(cis4mValue) ? cis4mValue.toJS() : cis4mValue;
    const selectedMetaKatItem = filteredItems.find(findMetaKatItemByValue(value, valuePath));

    return (
        <MetaKatItemBasicSelect
            className={cx('CIS4mMetaKatDropDown', props.className)}
            errorMessage={errorMessage}
            disabled={disabled}
            favorites={props.favorites}
            // Do not need to pass filter because filtering is done in this component
            // filter={props.filter}
            label={props.label}
            labelPath={props.labelPath}
            noValueLabel={props.noValueLabel}
            onChange={handleChange}
            options={filteredItems}
            path={props.path}
            readOnly={props.readonly}
            required={required}
            sort={props.sort}
            value={selectedMetaKatItem}
            valuePath={props.valuePath}
            variant={variant}
        />
    );
});

CIS4mMetaKatDropDown.displayName = 'CIS4mMetaKatDropDown';
